import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface CustomerListItem {
  customerKey: string
  name: string
  phone: string | null
  email: string | null
  ordersCount: number
  totalSpent: number
  currency: string
  firstOrderAt: string
  lastOrderAt: string
  lastOrderNumber: string
  lastStatus: string
}

export interface CustomerOrderItem {
  id: string
  orderNumber: string
  status: string
  type: string
  total: number
  currency: string
  createdAt: string
  locationName: string | null
}

export interface CustomerListMeta {
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface CustomerListResponse {
  data: CustomerListItem[]
  meta: CustomerListMeta
}

export interface CustomerDetail extends CustomerListItem {
  orders: CustomerOrderItem[]
  meta: CustomerListMeta
}

export interface ListCustomersQuery {
  page?: number
  pageSize?: number
  search?: string
}

export async function getCustomers(
  organizationId: string,
  query: ListCustomersQuery = {},
): Promise<CustomerListResponse> {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  if (query.search) params.set('search', query.search)
  const qs = params.toString()
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/customers${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load customers')
  return res.json() as Promise<CustomerListResponse>
}

export async function getCustomer(
  organizationId: string,
  customerKey: string,
  query: { page?: number; pageSize?: number } = {},
): Promise<CustomerDetail> {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  const qs = params.toString()
  const res = await fetch(
    `${API_BASE}/organizations/${organizationId}/customers/${encodeURIComponent(customerKey)}${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load customer')
  const body = await res.json()
  return body.data as CustomerDetail
}
